import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmpresa } from '../contexts/EmpresaContext';
// Icons will be inline SVGs
import api from '../api/config';
import '../styles/SuperAdminCompanySelector.css';

const SuperAdminCompanySelector = () => {
  const { empresaActiva, cambiarEmpresa } = useEmpresa();
  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Cargar empresas disponibles
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        const response = await api.get('/superadmin/companies');
        setCompanies(response.data);
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener color del tema y actualizar en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const primaryColor = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(primaryColor);
    };

    // Actualizar inicialmente
    updateThemeColor();

    // Escuchar cambios en localStorage (cambios desde otras pestañas)
    const handleStorageChange = () => {
      updateThemeColor();
    };

    // Escuchar evento personalizado para cambios locales
    const handleThemeChange = () => {
      updateThemeColor();
    };

    // Actualizar cada 200ms para capturar cambios inmediatos
    const interval = setInterval(updateThemeColor, 200);

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('theme-color-change', handleThemeChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('theme-color-change', handleThemeChange);
    };
  }, []);

  const handleCompanySelect = (company) => {
    console.log('🔄 SuperAdmin seleccionando empresa:', company);

    // Mapear la empresa del backend al formato que espera el contexto
    const empresaFormateada = {
      id: company.id,
      nombreFantasia: company.tradeName,
      razonSocial: company.legalName,
      rut: company.taxId,
      tradeName: company.tradeName,
      taxId: company.taxId,
      rolUsuario: 'Super Admin',
      role: 'admin',
      permisos: {
        crear_cotizaciones: true,
        aprobar_cotizaciones: true,
        ver_margenes: true,
        ver_costos: true,
        gestionar_usuarios: true,
        configurar_empresa: true,
        todos: true
      },
      permissions: {
        crear_cotizaciones: true,
        aprobar_cotizaciones: true,
        ver_margenes: true,
        ver_costos: true,
        gestionar_usuarios: true,
        configurar_empresa: true,
        todos: true
      }
    };

    // Guardar directamente en localStorage ANTES de cualquier otra acción
    localStorage.setItem('empresaActiva', JSON.stringify(empresaFormateada));
    console.log('✅ Empresa guardada en localStorage:', empresaFormateada);

    setIsOpen(false);

    // Pequeño delay para asegurar que localStorage se guardó
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 100);
  };

  const goToSuperAdminPanel = () => {
    setIsOpen(false);
    navigate('/super-admin');
  };

  // Función para determinar si un color es claro u oscuro
  const isLightColor = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  const textColor = isLightColor(themeColor) ? '#000' : '#fff';

  return (
    <div className="super-admin-company-selector" ref={dropdownRef}>
      <button
        className="super-admin-selector-btn"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: isOpen ? '#000' : themeColor,
          transition: 'background 0.2s ease, color 0.2s ease'
        }}
        ref={(el) => {
          if (el) {
            el.style.setProperty('color', isOpen ? themeColor : textColor, 'important');
          }
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isOpen ? '#000' : themeColor;
          e.currentTarget.style.setProperty('color', isOpen ? themeColor : textColor, 'important');
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isOpen ? '#000' : themeColor;
          e.currentTarget.style.setProperty('color', isOpen ? themeColor : textColor, 'important');
        }}
      >
        {empresaActiva ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
        )}
        <span>{empresaActiva ? empresaActiva.nombreFantasia : 'Seleccionar Empresa'}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div className="super-admin-dropdown">
          {/* Dropdown header removed for simplicity */}

          <div className="companies-list">
            {loading ? (
              <div className="loading-state">
                <span>Cargando empresas...</span>
              </div>
            ) : companies.length === 0 ? (
              <div className="empty-state">
                <span>No hay empresas disponibles</span>
              </div>
            ) : (
              companies.map((company) => (
                <div
                  key={company.id}
                  className={`company-option ${empresaActiva && empresaActiva.id === company.id ? 'active' : ''}`}
                  onClick={() => handleCompanySelect(company)}
                  style={empresaActiva && empresaActiva.id === company.id ? { background: themeColor, color: textColor } : {}}
                >
                  <div className="company-icon" style={{ background: themeColor }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <path d="M3 9h18"></path>
                      <path d="M9 21V9"></path>
                    </svg>
                  </div>
                  <div className="company-details">
                    <div className="company-name">{company.tradeName}</div>
                    <div className="company-info">
                      <span className="company-tax-id">{company.taxId}</span>
                      <span className="company-users">{company.userCount || 0} usuarios</span>
                    </div>
                  </div>
                  {empresaActiva && empresaActiva.id === company.id ? (
                    <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={themeColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="access-icon">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="dropdown-footer">
            <button
              className="super-admin-panel-btn"
              onClick={goToSuperAdminPanel}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"></path>
              </svg>
              <span>Panel Super Admin</span>
            </button>
            {empresaActiva && (
              <>
                <div className="exit-separator"></div>
                <button
                  className="exit-company-btn"
                  onClick={() => {
                    cambiarEmpresa(null);
                    setIsOpen(false);
                    navigate('/super-admin');
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span>Salir de empresa</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCompanySelector;